import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CheckCircle, ArrowLeft, User, MapPin, Calendar, SprayCan, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api';

const days = [
  { id: 'lundi', label: 'Lun' },
  { id: 'mardi', label: 'Mar' },
  { id: 'mercredi', label: 'Mer' },
  { id: 'jeudi', label: 'Jeu' },
  { id: 'vendredi', label: 'Ven' }
];

const timeSlots = [
  { id: 'am', label: 'AM', hours: '8h - 12h' },
  { id: 'pm', label: 'PM', hours: '12h - 17h' }
];

const cleaningServices = [
  { value: 'echangeur_air', label: 'Nettoyage d\'échangeur d\'air' },
  { value: 'thermopompe_centrale', label: 'Nettoyage de thermopompe centrale' },
  { value: 'thermopompe_murale', label: 'Nettoyage de thermopompe murale' }
];

export const CleaningRequestPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    service_type: [],
    description: '',
    availability: {}
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleServiceType = (value) => {
    setFormData(prev => ({
      ...prev,
      service_type: prev.service_type.includes(value)
        ? prev.service_type.filter(s => s !== value)
        : [...prev.service_type, value]
    }));
  };

  const toggleAvailability = (day, time) => {
    const key = `${day}_${time}`;
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [key]: !prev.availability[key]
      }
    }));
  };

  const getAvailabilityText = () => {
    const selected = Object.entries(formData.availability)
      .filter(([_, value]) => value)
      .map(([key]) => {
        const [day, time] = key.split('_');
        const dayLabel = days.find(d => d.id === day)?.label;
        return `${dayLabel} ${time.toUpperCase()}`;
      });
    return selected.length > 0 ? selected.join(', ') : 'Aucune disponibilité sélectionnée';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone || !formData.address) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (formData.service_type.length === 0) {
      toast.error('Veuillez sélectionner au moins un type de nettoyage');
      return;
    }

    const hasAvailability = Object.values(formData.availability).some(v => v);
    if (!hasAvailability) {
      toast.error('Veuillez sélectionner au moins une disponibilité');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/cleaning-requests`, {
        ...formData,
        availability_text: getAvailabilityText()
      });
      setSubmitted(true);
      toast.success('Votre demande a été envoyée avec succès!');
    } catch (error) {
      console.error('Error submitting cleaning request:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center py-20">
        <div className="max-w-md mx-auto text-center px-4">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-[#1a3a5c] mb-4">
            Demande envoyée!
          </h1>
          <p className="text-slate-600 mb-8">
            Merci pour votre demande de nettoyage. Notre équipe vous contactera 
            dans les plus brefs délais pour confirmer votre rendez-vous.
          </p>
          <Button 
            onClick={() => navigate('/')}
            className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8] text-white px-8"
            data-testid="back-home-btn"
          >
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="hero-gradient py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-72 h-72 bg-[#00b4d8] rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/services#nettoyage" className="inline-flex items-center text-slate-300 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux services
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center">
              <SprayCan className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              Demande de nettoyage
            </h1>
          </div>
          <p className="text-lg text-slate-300 max-w-2xl">
            Remplissez le formulaire ci-dessous et nous vous contacterons pour confirmer votre rendez-vous.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-xl p-8 md:p-10 space-y-8">
            
            {/* Contact Info */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-[#1a3a5c] flex items-center gap-2">
                <User className="w-5 h-5 text-[#00b4d8]" />
                Vos coordonnées
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">Prénom *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="mt-1"
                    data-testid="first-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Nom *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="mt-1"
                    data-testid="last-name-input"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Courriel *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="pl-10"
                      data-testid="email-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone">Téléphone *</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="pl-10"
                      placeholder="(450) 555-1234"
                      data-testid="phone-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-semibold text-[#1a3a5c] flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#00b4d8]" />
                Adresse
              </h3>
              <div>
                <Label htmlFor="address">Adresse complète *</Label>
                <Textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  rows={2}
                  placeholder="Numéro, rue, ville, code postal"
                  className="mt-1"
                  data-testid="address-input"
                />
              </div>
            </div>

            {/* Service Type */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-semibold text-[#1a3a5c] flex items-center gap-2">
                <SprayCan className="w-5 h-5 text-[#00b4d8]" />
                Type de nettoyage *
              </h3>
              <div className="space-y-3">
                {cleaningServices.map((service) => (
                  <button
                    key={service.value}
                    type="button"
                    onClick={() => toggleServiceType(service.value)}
                    className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${
                      formData.service_type.includes(service.value)
                        ? 'border-[#00b4d8] bg-[#00b4d8]/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      formData.service_type.includes(service.value)
                        ? 'bg-[#00b4d8] border-[#00b4d8]'
                        : 'border-slate-300'
                    }`}>
                      {formData.service_type.includes(service.value) && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className="font-medium text-[#1a3a5c]">{service.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Availability Grid */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-semibold text-[#1a3a5c] flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#00b4d8]" />
                Vos disponibilités *
              </h3>
              <p className="text-sm text-slate-500">Cochez les plages horaires où vous êtes disponible</p>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="p-2 text-left text-sm font-medium text-slate-500"></th>
                      {days.map((day) => (
                        <th key={day.id} className="p-2 text-center text-sm font-semibold text-[#1a3a5c]">
                          {day.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.map((slot) => (
                      <tr key={slot.id}>
                        <td className="p-2 text-sm font-medium text-slate-600">
                          <div>{slot.label}</div>
                          <div className="text-xs text-slate-400">{slot.hours}</div>
                        </td>
                        {days.map((day) => {
                          const isSelected = formData.availability[`${day.id}_${slot.id}`];
                          return (
                            <td key={`${day.id}_${slot.id}`} className="p-2 text-center">
                              <button
                                type="button"
                                onClick={() => toggleAvailability(day.id, slot.id)}
                                aria-label={`${day.label || day.id} ${slot.label || slot.id}`}
                                aria-pressed={Boolean(isSelected)}
                                className={`w-10 h-10 rounded-lg border-2 transition-all ${
                                  isSelected
                                    ? 'bg-[#00b4d8] border-[#00b4d8] text-white'
                                    : 'border-slate-200 hover:border-[#00b4d8] text-slate-400'
                                }`}
                              >
                                {isSelected && (
                                  <svg className="w-5 h-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Additional Notes */}
            <div className="space-y-4 pt-4 border-t">
              <Label htmlFor="description">Notes additionnelles (optionnel)</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                placeholder="Informations supplémentaires, accès particulier, nombre d'unités à nettoyer..."
                className="mt-1"
                data-testid="description-input"
              />
            </div>

            <p className="text-sm text-slate-500">
              En transmettant ce formulaire, vous consentez à l’utilisation de vos renseignements pour traiter votre demande. Consultez notre <a href="/politique-confidentialite" className="text-[#0077b6] underline">politique de confidentialité</a>.
            </p>

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-[#00b4d8] hover:bg-[#009bb8] text-white py-6 text-lg"
              data-testid="submit-btn"
            >
              {loading ? 'Envoi en cours...' : 'Envoyer la demande'}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default CleaningRequestPage;
